import { useCallback, useEffect, useRef, useState } from 'react';
import { checkDiscountCode, type CodeCart, type DiscountRefusal } from '../api/discountCode';
import { useAutomaticDiscount } from './useAutomaticDiscount';
import { useDiscountOffers } from './useDiscountOffers';
import { forgetDiscountCode, keepDiscountCode, keptDiscountCode } from '../commerce/sharedDiscount';

// The checkout's discounts: whether the store offers a code, the code applied and what it takes off this cart, or
// why a typed code was refused, and the store's best automatic discount. Discounts do not combine, so the saving
// shown is the bigger of the two, as the server prices the order.

/** A code that applies, and what it takes off the cart as it stands. */
export interface AppliedCode {
  code: string;
  amount: number;
}

/** The one saving the order gets: a code's, or an automatic discount's under its title. */
export type CheckoutSaving = { code: string | null; title: string; amount: number };

/** Everything the field and the totals read, and the ways to change it. */
export interface DiscountCodeState {
  available: boolean;
  applied: AppliedCode | null;
  /** The saving the totals show, the bigger of the applied code and the best automatic discount. */
  saving: CheckoutSaving | null;
  /** True when a code applies but an automatic discount saves more, so the code is not the one used. */
  beaten: boolean;
  refusal: DiscountRefusal | 'FAILED' | null;
  busy: boolean;
  apply: (typed: string) => void;
  remove: () => void;
  /** Takes the code off after the server refused it at ordering, saying why. */
  refuse: (reason: DiscountRefusal) => void;
}

// Applies the visit's code once — a shared link's, or one applied before the form was drawn anew — when codes are on offer.
function useReapplyKept(enabled: boolean, cartKey: string, run: (code: string) => Promise<void>): void {
  const tried = useRef(false);
  useEffect(() => {
    const kept = keptDiscountCode();
    if (tried.current || !enabled || !kept) return;
    tried.current = true;
    void run(kept);
  }, [enabled, cartKey, run]);
}

// Picks the saving the order gets: the code's unless the automatic discount takes off more, as the server does.
function pickSaving(applied: AppliedCode | null, automatic: { title: string; totalDiscount: number } | null): CheckoutSaving | null {
  if (automatic && automatic.totalDiscount > (applied?.amount ?? 0)) return { code: null, title: automatic.title, amount: automatic.totalDiscount };
  return applied ? { code: applied.code, title: applied.code, amount: applied.amount } : null;
}

// Holds the code for a cart, null in a preview: applies a typed one, and prices it again whenever the cart changes.
export function useDiscountCode(cart: CodeCart | null): DiscountCodeState {
  const offers = useDiscountOffers(cart !== null);
  const automatic = useAutomaticDiscount(cart, offers.automatic);
  const [applied, setApplied] = useState<AppliedCode | null>(null);
  const [refusal, setRefusal] = useState<DiscountCodeState['refusal']>(null);
  const [busy, setBusy] = useState(false);
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const latest = useRef(0);

  // Checks a code against the cart as it stands; only the newest check's answer is kept.
  const run = useCallback(async (typed: string) => {
    const current = cartRef.current;
    const code = typed.trim().toUpperCase();
    if (!current || !code) return;
    const ticket = ++latest.current;
    setBusy(true);
    setRefusal(null);
    try {
      const check = await checkDiscountCode(code, current);
      if (ticket !== latest.current) return;
      setApplied(check.isValid ? { code: check.code, amount: check.totalDiscount } : null);
      if (check.isValid) keepDiscountCode(check.code);
      else { setRefusal(check.reason); forgetDiscountCode(); }
    } catch {
      if (ticket === latest.current) setRefusal('FAILED');
    } finally {
      if (ticket === latest.current) setBusy(false);
    }
  }, []);

  // Prices the applied code again when the lines or the delivery fee move, so the total never shows an old saving.
  const appliedCode = useRef<string | null>(null);
  appliedCode.current = applied?.code ?? null;
  const cartKey = cart ? JSON.stringify(cart) : '';
  useEffect(() => { if (appliedCode.current) void run(appliedCode.current); }, [cartKey, run]);

  const saving = pickSaving(applied, automatic);
  useReapplyKept(offers.codes && cart !== null, cartKey, run);
  return {
    available: offers.codes, applied, saving, beaten: Boolean(applied && saving && saving.code === null), refusal, busy,
    apply: (typed) => void run(typed),
    remove: () => { latest.current += 1; setApplied(null); setRefusal(null); setBusy(false); forgetDiscountCode(); },
    refuse: (reason) => { setApplied(null); setRefusal(reason); forgetDiscountCode(); }
  };
}
